// src/Pages/PariwarMembers/SuccessMembers.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import {
    FaEye,
    FaEdit,
    FaTrash,
    FaUserTie,
    FaCheckCircle,
    FaTimesCircle,
    FaUserCheck,
    FaUsers,
    FaFileExcel
} from 'react-icons/fa';
import { useDispatch } from "react-redux";
import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import {
    useGetSuccessUsersQuery,
    useUpdateUserStatusMutation,
} from '../../redux/services/pariwarmember/pariwar.Services';
import * as XLSX from 'xlsx';
import CommonBreadcrumb from '../../common/CommonBreadcrumb';

function SuccessMembers() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);

    // ✅ 1. localStorage से role और waad_id लें
    const [userRole, setUserRole] = useState('');
    const [userWaadId, setUserWaadId] = useState('');

    useEffect(() => {
        try {
            const role = localStorage.getItem('userRole');
            const waadId = localStorage.getItem('userWaadId');
            
            if (role) setUserRole(role);
            if (waadId) setUserWaadId(waadId);
            
            console.log("🔍 User Role from localStorage:", role);
            console.log("🔍 User Waad ID from localStorage:", waadId);
        } catch (error) {
            console.error("Error reading localStorage:", error);
        }
    }, []);

    // ✅ 2. API Call with role and waad_id
    const {
        data: successData,
        isLoading,
        isError,
        error,
        refetch
    } = useGetSuccessUsersQuery({ 
        waad_id: userWaadId, 
        role: userRole 
    });

    const [updateUserStatus, { isLoading: isUpdating }] = useUpdateUserStatusMutation();

    const handleUpdateStatus = async (userId, status) => {
        try {
            await updateUserStatus({ id: userId, status }).unwrap();
            successToast(`User status updated successfully`);
            refetch();
        } catch (error) {
            errorToast(error?.data?.message || 'Failed to update status');
        }
    };

    const toggleUserSelection = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedUsers(successData?.data?.map(u => u.id) || []);
        } else {
            setSelectedUsers([]);
        }
    };

    const handleBulkReject = async () => {
        if (selectedUsers.length === 0) {
            errorToast('Please select at least one user');
            return;
        }

        try {
            for (const userId of selectedUsers) {
                await updateUserStatus({ id: userId, status: 3 }).unwrap();
            }
            successToast(`${selectedUsers.length} users rejected successfully`);
            setSelectedUsers([]);
            refetch();
        } catch (error) {
            errorToast(error?.data?.message || 'Bulk rejection failed');
        }
    };

    const handleBulkPending = async () => {
        if (selectedUsers.length === 0) {
            errorToast('Please select at least one user');
            return;
        }

        try {
            for (const userId of selectedUsers) {
                await updateUserStatus({ id: userId, status: 2 }).unwrap();
            }
            successToast(`${selectedUsers.length} users moved to pending`);
            setSelectedUsers([]);
            refetch();
        } catch (error) {
            errorToast(error?.data?.message || 'Bulk update failed');
        }
    };

    const handleViewUser = (userId) => {
        navigate(`/pariwar-view/${userId}`);
    };

    const handleEditUser = (user) => {
        dispatch(
            openModal({
                type: "edit-pariwar-member",
                title: "Edit Member",
                data: user
            })
        );
    };

    const handleDeleteUser = (user) => {
        dispatch(
            openModal({
                type: "delete-pariwar-member",
                title: "Delete Member",
                data: user
            })
        );
    };

    // Export to Excel function
    const exportToExcel = () => {
        if (!successData?.data || successData.data.length === 0) {
            errorToast('No data to export');
            return;
        }

        // Prepare data for Excel
        const exportData = successData.data.map(user => ({
            'ID': user.id || '-',
            'Name': user.name || '-',
            'Pariwar ID': user.pariwar_unique_id || '-',
            'Sadasya ID': user.sadasya_unique_id || '-',
            'Father Name': user.father_name || '-',
            'Mother Name': user.mother_name || '-',
            'Mobile': user.mobile || '-',
            'WhatsApp': user.wp_number || '-',
            'Email': user.email || '-',
            'Aadhar Number': user.aadhar_number || '-',
            'Date of Birth': user.dob ? new Date(user.dob).toLocaleDateString() : '-',
            'Gender': user.gender || '-',
            'Marriage Status': user.marrige_status || '-',
            'Marriage Year': user.marrige_year ? new Date(user.marrige_year).getFullYear() : '-',
            'Blood Group': user.blood_group || '-',
            'Education': user.education || '-',
            'Business': user.business || '-',
            'Job Position': user.job_position || '-',
            'Department': user.department || '-',
            'Posted Place': user.posted_place || '-',
            'Working Address': user.working_address || '-',
            'Annual Income': user.anual_income ? `₹${parseInt(user.anual_income).toLocaleString()}` : '-',
            'Relations': user.relations || '-',
            'Self Gotra': user.self_gotra || '-',
            'Sadasya Gotra': user.sadasya_gotra || '-',
            'Rishi Pravar Gotra': user.rishi_pravar_gotra || '-',
            'Mother Gotra': user.mother_gotra || '-',
            'Dadi Gotra': user.dadi_gotra || '-',
            'Nani Gotra': user.nani_gotra || '-',
            'Dada Name': user.dada_name || '-',
            'Dadi Name': user.dadi_name || '-',
            'Nana Name': user.nana_name || '-',
            'Nani Name': user.nani_name || '-',
            'Country': user.country_name || '-',
            'State': user.state_name || '-',
            'City': user.city_name || '-',
            'Tehsil': user.tehsil_name || '-',
            'Village': user.village_name || '-',
            'Ward': user.ward_name_number || '-',
            'Pin Code': user.pin_code || '-',
            'Native Village': user.native_village || '-',
            'Location Address': user.location_address || '-',
            'Location Latitude': user.location_latitude || '-',
            'Location Longitude': user.location_longitude || '-',
            'Location Accuracy': user.location_accuracy || '-',
            'Status': user.status === '1' ? 'Approved' : user.status === '0' ? 'Pending' : user.status === '2' ? 'Rejected' : '-',
            'Created At': user.created_at ? new Date(user.created_at).toLocaleString() : '-',
            'Updated At': user.updated_at ? new Date(user.updated_at).toLocaleString() : '-'
        }));

        // Create workbook
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        
        // Set column widths
        const colWidths = [];
        if (exportData.length > 0) {
            Object.keys(exportData[0]).forEach((key, index) => {
                colWidths[index] = { wch: Math.max(key.length, 15) };
            });
            ws['!cols'] = colWidths;
        }
        
        XLSX.utils.book_append_sheet(wb, ws, 'Approved Members');
        
        // Generate Excel file
        const fileName = `Approved_Members_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
        successToast(`Exported ${exportData.length} members to Excel`);
    };

    // Filter data based on search
    const filteredData = successData?.data?.filter(user => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            user.name?.toLowerCase().includes(searchLower) ||
            user.mobile?.toLowerCase().includes(searchLower) ||
            user.email?.toLowerCase().includes(searchLower) ||
            user.sadasya_unique_id?.toLowerCase().includes(searchLower)
        );
    }) || [];

    // Loading state
    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-success" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Error state
    if (isError) {
        return (
            <div className="alert alert-danger m-3">
                Error: {error?.data?.message || 'Something went wrong'}
            </div>
        );
    }

    return (
        <div>

            {/* =============================================
                PAGE HEADER
                ============================================= */}
            {/* <div className="page-header-breadcrumb all_breadcrumb">
                <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
                    <nav>
                        <h4 className="fw-semibold">
                            <FaUserCheck className="me-2 text-success" />
                            Approved Members
                        </h4>
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                                <Link to="/">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <Link to="/pariwar-members">Members</Link>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                Approved
                            </li>
                        </ol>
                    </nav>
                </div>
                <div className="page-title fw-semibold fs-18 mb-0">
                    <button 
                        className="btn btn-success me-2"
                        onClick={exportToExcel}
                        disabled={!successData?.data || successData.data.length === 0}
                    >
                        <FaFileExcel className="me-2" />
                        Download Excel
                    </button>
                    <button 
                        className="btn btn-primary"
                        onClick={() => refetch()}
                    >
                        <FaUsers className="me-2" />
                        Refresh
                    </button>
                </div>
            </div> */}
<CommonBreadcrumb
    title="Approved Members"
    titleIcon={<FaUserCheck className="me-2 text-success" />}
    breadcrumbs={[
        { label: "Home", link: "/" },
        { label: "Members", link: "/pariwar-members" },
        { label: "Approved" },
    ]}
    buttons={[
        {
            text: "Download Excel",
            icon: <FaFileExcel className="me-2" />,
            onClick: exportToExcel,
            className: "btn btn-primary",
            disabled: !successData?.data || successData.data.length === 0,
        },
        {
            text: "Refresh",
            icon: <FaUsers className="me-2" />,
            onClick: refetch,
            className: "btn btn-primary",
        },
    ]}
/>
            {/* =============================================
                STATUS SUMMARY
                ============================================= */}
            <div className="card bg-success bg-opacity-10 border-success mb-4">
                <div className="card-body d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="mb-1">Total Approved Members</h5>
                        <h2 className="mb-0">{successData?.count || 0}</h2>
                    </div>
                    <div className="bg-success bg-opacity-25 p-4 rounded-circle">
                        <FaUserCheck className="text-success fs-1" />
                    </div>
                </div>
            </div>

            {/* =============================================
                SEARCH & BULK ACTIONS
                ============================================= */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name, mobile, email, ID..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="col-md-6 text-end">
                            <button 
                                className="btn btn-warning me-2"
                                onClick={handleBulkPending}
                                disabled={selectedUsers.length === 0 || isUpdating}
                            >
                                <FaTimesCircle className="me-2" />
                                Move to Pending ({selectedUsers.length})
                            </button>
                            <button 
                                className="btn btn-danger"
                                onClick={handleBulkReject}
                                disabled={selectedUsers.length === 0 || isUpdating}
                            >
                                <FaTimesCircle className="me-2" />
                                Reject Selected ({selectedUsers.length})
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* =============================================
                TABLE
                ============================================= */}
            <div className="st-wrapper-card">
                <div className="st-table-responsive">
                    <table className="table st-custom-table align-middle mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: '40px' }}>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        onChange={handleSelectAll}
                                        checked={selectedUsers.length === filteredData.length && filteredData.length > 0}
                                    />
                                </th>
                                <th>#ID</th>
                                <th>Name</th>
                                <th>Mobile</th>
                                <th>Email</th>
                                <th>Gotra</th>
                                <th className="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-4">
                                        <div className="text-muted">
                                            <FaUserCheck className="fs-2 d-block mx-auto mb-2" />
                                            No approved members found
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((user) => (
                                    <tr key={user.id}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={selectedUsers.includes(user.id)}
                                                onChange={() => toggleUserSelection(user.id)}
                                            />
                                        </td>
                                        <td>#{user.id}</td>
                                        <td className="fw-semibold">{user.name}</td>
                                        <td>{user.mobile || '-'}</td>
                                        <td>{user.email || '-'}</td>
                                        <td>{user.self_gotra || '-'}</td>
                                        <td>
                                            <div className="d-flex justify-content-center gap-2">
                                                {/* Status Dropdown */}
                                                <select
                                                    className="form-select form-select-sm"
                                                    style={{ width: '120px' }}
                                                    onChange={(e) => handleUpdateStatus(user.id, parseInt(e.target.value))}
                                                    value={user.status}
                                                    disabled={isUpdating}
                                                >
                                                    <option value="2">⏳ Pending</option>
                                                    <option value="1">✅ Success</option>
                                                    <option value="3">❌ Reject</option>
                                                </select>

                                                {/* View Button */}
                                                <button
                                                    className="st-action-btn st-view-btn"
                                                    onClick={() => handleViewUser(user.id)}
                                                    title="View Details"
                                                >
                                                    <FaEye />
                                                </button>
                                                <Link
                                                    to={`/edit-user/${user.roles}/${user.id}`}
                                                    className="st-action-btn st-edit-btn"
                                                    title="Edit Member"
                                                >
                                                    <FaEdit />
                                                </Link>

                                                {/* Delete Button */}
                                                <button
                                                    className="st-action-btn st-delete-btn"
                                                    onClick={() => handleDeleteUser(user)}
                                                    title="Delete Member"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
}

export default SuccessMembers;